class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

if (!customElements.get('free-shipping-banner')) {
  customElements.define(
    'free-shipping-banner',
    class FreeShippingBanner extends HTMLElement {
      connectedCallback() {
        this.copyButton = this.querySelector('[data-free-shipping-copy]');
        this.copyButton?.addEventListener('click', this.copyCode);
        this.render(Number(this.dataset.qualifyingItemsCount || 0));

        if (typeof subscribe === 'function') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            const items = event?.cartData?.items;
            if (!Array.isArray(items)) return;
            const qualifyingItemsCount = items.reduce(
              (count, item) => count + (item.sku === 'PREMIER' ? 0 : Number(item.quantity || 0)),
              0
            );
            this.render(qualifyingItemsCount);
          });
        }
      }

      disconnectedCallback() {
        this.copyButton?.removeEventListener('click', this.copyCode);
        this.cartUpdateUnsubscriber?.();
        clearTimeout(this.copiedMessageTimeout);
      }

      copyCode = async () => {
        const code = this.dataset.code || '';
        if (!code) return;

        let copied = false;
        try {
          if (!navigator.clipboard?.writeText) throw new Error('clipboard_unavailable');
          await navigator.clipboard.writeText(code);
          copied = true;
        } catch (_error) {
          try {
            const textarea = document.createElement('textarea');
            textarea.value = code;
            textarea.setAttribute('aria-hidden', 'true');
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            copied = document.execCommand('copy');
            textarea.remove();
          } catch (_fallbackError) {
            copied = false;
          }
        }

        const copiedMessage = this.querySelector('[data-free-shipping-copied]');
        if (!copiedMessage) return;
        copiedMessage.textContent = copied ? this.dataset.copiedMessage || '' : this.dataset.copyFailedMessage || '';
        copiedMessage.hidden = false;
        clearTimeout(this.copiedMessageTimeout);
        if (copied) {
          this.copiedMessageTimeout = setTimeout(() => {
            copiedMessage.hidden = true;
          }, 1200);
        }
        this.copyButton?.focus();
      };

      render(qualifyingItemsCount) {
        const threshold = Number(this.dataset.threshold || 0);
        if (!threshold) return;

        const qualifies = qualifyingItemsCount >= threshold;
        const itemsRemaining = Math.max(threshold - qualifyingItemsCount, 0);
        const progressPercentage = Math.min((qualifyingItemsCount / threshold) * 100, 100);
        const message = this.querySelector('[data-free-shipping-message-text]');
        const codeRow = this.querySelector('[data-free-shipping-code-row]');

        if (message) {
          message.textContent = qualifies
            ? this.dataset.qualifiedMessage || ''
            : (this.dataset.remainingTemplate || '').replace('__COUNT__', String(itemsRemaining));
        }
        if (codeRow) {
          codeRow.dataset.qualified = String(qualifies);
          codeRow.setAttribute('aria-hidden', String(!qualifies));
        }
        this.style.setProperty('--free-shipping-progress', `${progressPercentage}%`);
        this.dataset.qualifyingItemsCount = String(qualifyingItemsCount);
      }
    }
  );
}
